"use client";

import { type ReactNode, useState } from "react";
import {
  Checkbox,
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import {
  SUBSTANCE_STRINGS,
  SubstanceFields,
  SubstanceProblem,
} from "@/components/tools/chemistry/substance-fields";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  fewestSigFigs,
  formatPlain,
  formatSig,
} from "@/lib/tool-kit/chemistry/format";
import {
  type Equivalent,
  equivalents,
  findPartsUnit,
  PARTS_UNITS,
  toGramsPerLitre,
} from "@/lib/tool-kit/chemistry/parts";
import {
  resolveSubstance,
  type SubstanceMode,
} from "@/lib/tool-kit/chemistry/substance";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const PPM_CALCULATOR_STRINGS = {
  ...SUBSTANCE_STRINGS,
  valueLabel: "Concentration",
  unitLabel: "Unit",
  densityLabel: "Density of the solution (g/mL)",
  densityHint:
    "1.000 is right for dilute aqueous solutions; water is 0.9982 g/mL at 20 °C. It links mass fractions (ppm) to mg/L.",
  includeMolarity: "Include molarity (needs a formula or molar mass)",
  sigFigs: "Significant figures in the answer",
  sigMatch: "Match my data",
  sigChoice:
    "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  equivalentsHeading: "The same concentration in other units",
  columnUnit: "Unit",
  columnValue: "Value",
  working: "The working",
  toGramsFraction:
    "{value} {unit} = a mass fraction of {fraction}; × 1000 mL/L × {density} g/mL = {grams} g/L",
  toGramsMass: "{value} {unit} = {grams} g/L",
  toGramsMolar: "{value} {unit} × {molar} g/mol = {grams} g/L",
  toPpm: "ppm = {grams} g/L ÷ (1000 mL/L × {density} g/mL) × 10⁶ = {ppm}",
  toMolar: "mol/L = {grams} g/L ÷ {molar} g/mol = {molarity}",
  densityNote:
    "At a density of {density} g/mL, 1 ppm is {mgl} mg/L, not exactly 1.",
  needMolarMass:
    "Molar units need a molar mass: tick the box above and give a formula or molar mass.",
  enterValue: "Enter a concentration to convert.",
  invalidValue: "Enter a concentration of zero or more.",
  invalidDensity: "Enter a density greater than zero, in g/mL.",
  copy: "Copy conversions",
  copied: "Copied!",
} as const;

const SIG_CHOICES = ["3", "4", "5", "6"] as const;
const DEFAULT_SIG = 4;
const HIDDEN = new Set(["mgkg", "ugkg"]);
const UNIT_OPTIONS = PARTS_UNITS.map((u) => ({ value: u.id, label: u.symbol }));
const HEADLINE = ["ppm", "mgL", "pct"] as const;

export default function PpmCalculatorWidget() {
  const strings = useToolUi(PPM_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [valueText, setValueText] = useState("250.0");
  const [unit, setUnit] = useState("mgL");
  const [densityText, setDensityText] = useState("1.000");
  const [sigChoice, setSigChoice] = useState("match");
  const [withMolarity, setWithMolarity] = useState(true);
  const [mode, setMode] = useState<SubstanceMode>("formula");
  const [formula, setFormula] = useState("NaCl");
  const [molarMassText, setMolarMassText] = useState("");

  const value = parseMeasuredNumber(valueText);
  const density = parseMeasuredNumber(densityText);
  const substance = resolveSubstance(mode, formula, molarMassText);
  const molarMass =
    withMolarity && substance.kind === "ok" ? substance.molarMass : null;
  const unitDef = findPartsUnit(unit);
  const typed = [
    valueText,
    ...(withMolarity && mode === "mass" ? [molarMassText] : []),
  ];
  const sig =
    sigChoice === "match"
      ? fewestSigFigs(typed, DEFAULT_SIG)
      : Number(sigChoice);

  let body: ReactNode;
  if (valueText.trim() === "") {
    body = <Notice>{strings.enterValue}</Notice>;
  } else if (value === null || value < 0) {
    body = <ErrorNotice>{strings.invalidValue}</ErrorNotice>;
  } else if (density === null || !(density > 0)) {
    body = <ErrorNotice>{strings.invalidDensity}</ErrorNotice>;
  } else if (withMolarity && substance.kind !== "ok") {
    body = <SubstanceProblem strings={strings} substance={substance} />;
  } else if (unitDef.kind === "molar" && molarMass === null) {
    body = <Notice>{strings.needMolarMass}</Notice>;
  } else {
    const context = { density, molarMass };
    const rows = equivalents(value, unit, context);
    body =
      rows === null ? null : (
        <Conversions
          context={context}
          rows={rows}
          sig={sig}
          unit={unit}
          value={value}
        />
      );
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="ppm-value"
          label={strings.valueLabel}
          numeric
          onChange={setValueText}
          value={valueText}
        />
        <SelectField
          id="ppm-unit"
          label={strings.unitLabel}
          onChange={setUnit}
          options={UNIT_OPTIONS}
          value={unit}
        />
        <TextField
          hint={strings.densityHint}
          id="ppm-density"
          label={strings.densityLabel}
          numeric
          onChange={setDensityText}
          value={densityText}
        />
      </div>
      <Checkbox
        checked={withMolarity}
        label={strings.includeMolarity}
        onChange={setWithMolarity}
      />
      {withMolarity ? (
        <SubstanceFields
          formula={formula}
          formulaPlaceholder="NaCl"
          id="ppm"
          mode={mode}
          molarMass={molarMassText}
          molarMassPlaceholder="58.44"
          onFormulaChange={setFormula}
          onModeChange={setMode}
          onMolarMassChange={setMolarMassText}
          strings={strings}
        />
      ) : null}
      <SelectField
        id="ppm-sig"
        label={strings.sigFigs}
        onChange={setSigChoice}
        options={[
          { value: "match", label: strings.sigMatch },
          ...SIG_CHOICES.map((choice) => ({
            value: choice,
            label: tPlural(strings.sigChoice, locale, Number(choice), {
              count: fmt(Number(choice)),
            }).trim(),
          })),
        ]}
        value={sigChoice}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}

function Conversions({
  rows,
  value,
  unit,
  context,
  sig,
}: {
  rows: Equivalent[];
  value: number;
  unit: string;
  context: { density: number; molarMass: number | null };
  sig: number;
}) {
  const strings = useToolUi(PPM_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const from = findPartsUnit(unit);
  const grams = toGramsPerLitre(value, from, context) ?? 0;
  const shown = rows.filter(
    (row) => row.value !== null && !HIDDEN.has(row.unit.id)
  );
  const text = (row: Equivalent) =>
    `${formatSig(fmt, row.value ?? 0, sig)} ${row.unit.symbol}`;
  const headline = HEADLINE.map((id) =>
    rows.find((row) => row.unit.id === id)
  ).filter((row): row is Equivalent => row !== undefined && row.value !== null);
  const ppm = rows.find((row) => row.unit.id === "ppm")?.value ?? 0;
  const mgPerL = rows.find((row) => row.unit.id === "mgL")?.value ?? 0;
  const molar = rows.find((row) => row.unit.id === "M")?.value ?? null;
  const common = {
    value: formatPlain(fmt, value),
    unit: from.symbol,
    density: formatPlain(fmt, context.density),
    grams: formatSig(fmt, grams, sig + 1),
  };
  let first: string;
  if (from.kind === "fraction") {
    first = t(strings.toGramsFraction, {
      ...common,
      fraction: formatPlain(fmt, value * from.factor),
    });
  } else if (from.kind === "massConc") {
    first = t(strings.toGramsMass, common);
  } else {
    first = t(strings.toGramsMolar, {
      ...common,
      molar: formatPlain(fmt, context.molarMass ?? 0),
    });
  }
  const offDensity = Math.abs(context.density - 1) > 0.005;
  return (
    <>
      <StatGrid>
        {headline.map((row, index) => (
          <StatCard
            emphasis={index === 0}
            key={row.unit.id}
            label={row.unit.symbol}
            value={formatSig(fmt, row.value ?? 0, sig)}
          />
        ))}
      </StatGrid>
      {offDensity ? (
        <Notice>
          {t(strings.densityNote, {
            density: formatPlain(fmt, context.density),
            mgl: formatSig(fmt, mgPerL / ppm, 3),
          })}
        </Notice>
      ) : null}
      <div className="space-y-3">
        <h3 className="font-semibold text-foreground text-lg">
          {strings.equivalentsHeading}
        </h3>
        <DataTable
          head={[strings.columnUnit, strings.columnValue]}
          rows={shown.map((row) => [row.unit.symbol, text(row)])}
        />
      </div>
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>{first}</p>
        <p>
          {t(strings.toPpm, {
            grams: formatSig(fmt, grams, sig + 1),
            density: formatPlain(fmt, context.density),
            ppm: formatSig(fmt, ppm, sig),
          })}
        </p>
        {molar !== null && context.molarMass !== null ? (
          <p>
            {t(strings.toMolar, {
              grams: formatSig(fmt, grams, sig + 1),
              molar: formatPlain(fmt, context.molarMass),
              molarity: `${formatSig(fmt, molar, sig)} mol/L`,
            })}
          </p>
        ) : null}
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={shown.map(text).join("; ")}
      />
    </>
  );
}
